import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useMounted, useLayout } from '@websolutespa/bom-mixer-hooks';
import { forwardRef, useRef, useMemo, useImperativeHandle, useEffect } from 'react';
import { Toaster } from 'sonner';
import { useLlm } from '../useLlm/useLlm.js';
import { useLlmView } from '../useLlm/useLlmView.js';

const LlmComponent = /*#__PURE__*/forwardRef((props, ref) => {
  const innerRef = useRef(null);
  const mounted = useMounted();
  const {
    locale
  } = useLayout();
  const {
    Root,
    PageRoot
  } = useLlm(state => state.components);
  const {
    init,
    send,
    clear
  } = useLlm(state => state.actions);
  const ready = useLlm(state => state.ready);
  const app = useLlm(state => state.app);
  const theme = useLlm(state => state.theme);
  const embed = useLlm(state => state.embed);
  const {
    initView,
    initIntersection,
    open,
    isOpened,
    setOpened
  } = useLlmView(state => state.actions);
  const skipCustomIntro = useLlmView(state => state.skipCustomIntro);
  const idle = useLlmView(state => state.idle);
  const opened = useLlmView(state => state.opened);
  const introed = useLlmView(state => state.introed);
  const embedded = useLlmView(state => state.embedded);
  const imperative = useLlmView(state => state.imperative);
  const instance = useMemo(() => ({
    open: () => {
      open();
    },
    isOpened: () => {
      return isOpened();
    },
    setOpened: opened => {
      setOpened(opened);
    },
    send: message => {
      setOpened(true);
      send(message);
    },
    clear: () => {
      clear();
    }
  }), [open, isOpened, setOpened, send, clear]);
  useImperativeHandle(ref, () => instance);
  useEffect(() => {
    init(locale, instance);
    initView();
  }, [init, locale, instance, initView]);
  useEffect(() => {
    if (mounted && ready && innerRef.current) {
      initIntersection(innerRef.current);
    }
  }, [mounted, ready, initIntersection, innerRef]);
  useEffect(() => {
    if (!embedded && theme.mode !== 'page') {
      document.documentElement.style.overflow = opened ? 'hidden' : 'auto';
    }
  }, [opened, embedded, theme.mode]);
  const shouldShowTrigger = !opened && !idle && !embed && !imperative || Boolean((app === null || app === void 0 ? void 0 : app.contents.customIntro) && !introed);
  const mode = theme.mode || 'default';
  const rootProps = useMemo(() => ({
    opened,
    embedded,
    customIntro: Boolean((app === null || app === void 0 ? void 0 : app.contents.customIntro) && !skipCustomIntro),
    shouldShowTrigger,
    mode: mode
  }), [opened, embedded, app === null || app === void 0 ? void 0 : app.contents.customIntro, skipCustomIntro, shouldShowTrigger, mode]);
  // console.log(theme, mode, app?.contents?.customTheme);
  if (mounted && ready) {
    if (mode === 'page') {
      return jsxs(Fragment, {
        children: [jsx(PageRoot, Object.assign({
          ref: innerRef
        }, rootProps)), jsx(Toaster, {
          theme: theme.toaster
        })]
      });
    } else {
      return jsxs(Fragment, {
        children: [jsx(Root, Object.assign({
          ref: innerRef
        }, rootProps)), jsx(Toaster, {
          theme: theme.toaster
        })]
      });
    }
  } else {
    return jsx(Fragment, {});
  }
});
LlmComponent.displayName = 'LlmComponent';

export { LlmComponent };
//# sourceMappingURL=llm.js.map
